import { useEffect, useRef, useState } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { Icon } from "@/components/ui/Icon";
import { Popover } from "@/components/ui/Popover";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";

export interface LinkValue {
  url: string;
  label: string;
}

interface LinkCellProps {
  value: LinkValue | null;
  rowH?: number;
  onPatch?: (value: LinkValue | null) => void;
}

const MOBILE_MQ = "(max-width: 640px)";

function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || trimmed.startsWith("mailto:")) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function hostnameOf(rawUrl: string): string {
  try {
    return new URL(rawUrl).hostname;
  } catch {
    return rawUrl;
  }
}

export function LinkCell({ value, rowH = 44, onPatch }: LinkCellProps) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [draftUrl, setDraftUrl] = useState(value?.url ?? "");
  const [draftLabel, setDraftLabel] = useState(value?.label ?? "");
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const urlInputRef = useRef<HTMLInputElement | null>(null);
  const skipCloseCommitRef = useRef(false);

  const editable = Boolean(onPatch);

  const openEditor = () => {
    setDraftUrl(value?.url ?? "");
    setDraftLabel(value?.label ?? "");
    skipCloseCommitRef.current = false;
    setIsMobile(window.matchMedia(MOBILE_MQ).matches);
    setOpen(true);
  };

  const buildCommitValue = (): LinkValue | null => {
    const normalized = normalizeUrl(draftUrl);
    if (!normalized) return null;
    const label = draftLabel.trim() || hostnameOf(normalized);
    return { url: normalized, label };
  };

  const commitNow = () => {
    if (!onPatch) {
      setOpen(false);
      return;
    }
    const next = buildCommitValue();
    const changed =
      (next === null && value !== null) ||
      (next !== null && (next.url !== value?.url || next.label !== value?.label));
    if (changed) onPatch(next);
    skipCloseCommitRef.current = true;
    setOpen(false);
  };

  const revertAndClose = () => {
    skipCloseCommitRef.current = true;
    setOpen(false);
  };

  const removeValue = () => {
    if (onPatch && value !== null) onPatch(null);
    skipCloseCommitRef.current = true;
    setOpen(false);
  };

  const closeViaOutside = () => {
    if (skipCloseCommitRef.current) {
      setOpen(false);
      return;
    }
    commitNow();
  };

  const display = value
    ? value.label ||
      (() => {
        try {
          return new URL(value.url).hostname;
        } catch {
          return value.url;
        }
      })()
    : null;

  return (
    <>
      <CellWrap rowH={rowH} onClick={editable ? openEditor : undefined} interactive={editable}>
        <div
          ref={anchorRef}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {value ? (
            <a
              href={value.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                color: "var(--accent)",
                textDecoration: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{display}</span>
              <Icon name="ext" size={11} color="var(--ink-3)" />
            </a>
          ) : (
            <span
              style={{
                fontSize: 12.5,
                color: "var(--ink-4)",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
            >
              + {t.link}
            </span>
          )}
        </div>
      </CellWrap>
      {editable && open ? (
        isMobile ? (
          <MobileSheet onClose={closeViaOutside}>
            <LinkEditor
              draftUrl={draftUrl}
              draftLabel={draftLabel}
              setDraftUrl={setDraftUrl}
              setDraftLabel={setDraftLabel}
              onCommit={commitNow}
              onRevert={revertAndClose}
              onRemove={value !== null ? removeValue : undefined}
              urlInputRef={urlInputRef}
            />
          </MobileSheet>
        ) : (
          <Popover anchor={anchorRef.current} onClose={closeViaOutside} width={280}>
            <LinkEditor
              draftUrl={draftUrl}
              draftLabel={draftLabel}
              setDraftUrl={setDraftUrl}
              setDraftLabel={setDraftLabel}
              onCommit={commitNow}
              onRevert={revertAndClose}
              onRemove={value !== null ? removeValue : undefined}
              urlInputRef={urlInputRef}
            />
          </Popover>
        )
      ) : null}
    </>
  );
}

interface LinkEditorProps {
  draftUrl: string;
  draftLabel: string;
  setDraftUrl: (v: string) => void;
  setDraftLabel: (v: string) => void;
  onCommit: () => void;
  onRevert: () => void;
  onRemove?: () => void;
  urlInputRef: React.RefObject<HTMLInputElement | null>;
}

function LinkEditor({
  draftUrl,
  draftLabel,
  setDraftUrl,
  setDraftLabel,
  onCommit,
  onRevert,
  onRemove,
  urlInputRef,
}: LinkEditorProps) {
  useEffect(() => {
    urlInputRef.current?.focus();
    urlInputRef.current?.select();
  }, [urlInputRef]);

  const inputStyle = {
    height: 32,
    padding: "0 10px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--hairline-strong)",
    background: "var(--surface)",
    color: "var(--ink-1)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box" as const,
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--ink-3)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    marginBottom: 4,
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onRevert();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onCommit();
    }
  };

  return (
    <div style={{ padding: 10 }}>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>URL</label>
        <input
          ref={urlInputRef}
          type="url"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          value={draftUrl}
          placeholder="https://..."
          onChange={(e) => setDraftUrl(e.target.value)}
          onKeyDown={handleKey}
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Anzeigetext</label>
        <input
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={draftLabel}
          placeholder={draftUrl ? hostnameOf(normalizeUrl(draftUrl)) : "Label"}
          onChange={(e) => setDraftLabel(e.target.value)}
          onKeyDown={handleKey}
          style={inputStyle}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginTop: 4,
        }}
      >
        {onRemove ? (
          <button
            onClick={onRemove}
            style={{
              height: 32,
              padding: "0 12px",
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              color: "var(--ink-2)",
              background: "transparent",
              border: "1px solid transparent",
              borderRadius: 6,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            Entfernen
          </button>
        ) : (
          <span />
        )}
        <button
          onClick={onCommit}
          style={{
            height: 32,
            padding: "0 14px",
            fontSize: 12.5,
            fontWeight: 500,
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            color: "#fff",
            background: BRAND.blue,
            border: "1px solid transparent",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {t.save}
        </button>
      </div>
    </div>
  );
}

interface MobileSheetProps {
  onClose: () => void;
  children: React.ReactNode;
}

function MobileSheet({ onClose, children }: MobileSheetProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target as Node)) onClose();
    };
    const id = window.setTimeout(() => {
      document.addEventListener("mousedown", onDown);
      document.addEventListener("touchstart", onDown);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [onClose]);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 400,
        background: "rgba(13,62,99,0.18)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        ref={sheetRef}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          background: "var(--surface)",
          color: "var(--ink-1)",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          border: "1px solid var(--hairline-strong)",
          borderBottom: "none",
          boxShadow: "0 -10px 28px rgba(13,62,99,0.18)",
          fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        {children}
      </div>
    </div>
  );
}
